import { memo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { safeParseChartSpec, type ChartSpec } from '@shared/chart-spec'
import { LightweightChart } from '../chart/LightweightChart'
import { remarkCashtags } from '../../lib/remark-cashtags'
import { useStore } from '../../store'

type Segment =
  | { type: 'md'; text: string }
  | { type: 'chart'; spec: ChartSpec }
  | { type: 'pending' }

const CHART_RE = /```chart\s*\n([\s\S]*?)```/g

function segment(content: string): Segment[] {
  const segs: Segment[] = []
  let last = 0
  let m: RegExpExecArray | null
  CHART_RE.lastIndex = 0
  while ((m = CHART_RE.exec(content)) !== null) {
    if (m.index > last) segs.push({ type: 'md', text: content.slice(last, m.index) })
    let spec: ChartSpec | null = null
    try {
      spec = safeParseChartSpec(JSON.parse(m[1]))
    } catch {
      spec = null
    }
    segs.push(spec ? { type: 'chart', spec } : { type: 'md', text: m[0] })
    last = CHART_RE.lastIndex
  }
  let tail = content.slice(last)
  // Hide a chart block that is still streaming (opened but not yet closed).
  const open = tail.lastIndexOf('```chart')
  if (open !== -1 && tail.indexOf('```', open + 8) === -1) {
    if (open > 0) segs.push({ type: 'md', text: tail.slice(0, open) })
    segs.push({ type: 'pending' })
    tail = ''
  } else if (tail) {
    segs.push({ type: 'md', text: tail })
  }
  return segs
}

const mdComponents = {
  a: ({ href, className, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    // Cashtags ($ARM) are emitted as Yahoo links by remarkCashtags; intercept them
    // to open the in-app symbol detail popup instead of navigating away.
    const isTicker = typeof href === 'string' && href.includes('finance.yahoo.com/quote/')
    if (isTicker) {
      const ticker = decodeURIComponent((href as string).split('/quote/')[1] ?? '')
      return (
        <button type="button" className="ticker" title={`${ticker} — details`} onClick={() => useStore.getState().openSymbol(ticker)}>
          {children}
        </button>
      )
    }
    return (
      <a {...rest} href={href} target="_blank" rel="noreferrer noopener" className={className}>
        {children}
      </a>
    )
  },
  table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
    <div className="-mx-1 overflow-x-auto px-1">
      <table {...props} />
    </div>
  ),
}

export const Markdown = memo(function Markdown({ content }: { content: string }) {
  const segs = segment(content)
  return (
    <div className="prose">
      {segs.map((s, i) => {
        if (s.type === 'chart') return <LightweightChart key={i} spec={s.spec} />
        if (s.type === 'pending')
          return (
            <div
              key={i}
              className="card my-3 flex h-[120px] items-center justify-center text-[12px] text-fg-subtle"
            >
              <span className="animate-pulse">Preparing chart…</span>
            </div>
          )
        return (
          <ReactMarkdown key={i} remarkPlugins={[remarkGfm, remarkCashtags]} components={mdComponents}>
            {s.text}
          </ReactMarkdown>
        )
      })}
    </div>
  )
})
